export type JwtPayload = Record<string, unknown> & {
    exp?: number;
};

function base64UrlDecode(value: string): string | null {
    try {
        const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
        const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
        return atob(padded);
    } catch {
        return null;
    }
}

export function decodeJwtPayload(token: string): JwtPayload | null {
    // JWTs must have exactly three segments: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const decoded = base64UrlDecode(parts[1]);
    if (!decoded) return null;

    try {
        return JSON.parse(decoded) as JwtPayload;
    } catch {
        return null;
    }
}

export function getJwtExpiration(token: string): number | null {
    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload.exp !== "number") return null;
    return payload.exp;
}

export function getJwtExpirationFromUrl(url: string, paramName = "token"): number | null {
    try {
        const urlObj = new URL(url);
        const token = urlObj.searchParams.get(paramName);
        if (!token) return null;
        return getJwtExpiration(token);
    } catch {
        return null;
    }
}

export function isJwtExpired(expiration: number, bufferSeconds = 0): boolean {
    const now = Math.floor(Date.now() / 1000);
    return expiration <= now + bufferSeconds;
}

export function isJwtExpiredFromToken(token: string, bufferSeconds = 0): boolean {
    const exp = getJwtExpiration(token);
    if (exp === null) return true;
    return isJwtExpired(exp, bufferSeconds);
}

export function isJwtExpiredFromUrl(url: string, bufferSeconds = 0, paramName = "token"): boolean {
    const exp = getJwtExpirationFromUrl(url, paramName);
    if (exp === null) return true;
    return isJwtExpired(exp, bufferSeconds);
}
